import type { BountyCompany, BountyLocationPoint } from '../types/bounty';

export type { BountyLocationPoint };

export type BountyLocationFields = Pick<BountyCompany, 'locations' | 'isNational'>;

export type BountyCompanyLocationInput = {
  isNational?: boolean;
  locations?: BountyLocationPoint[];
};

/** All geocoded anchors on a bounty company. */
export function getBountyLocationPoints(entity: BountyLocationFields): BountyLocationPoint[] {
  return (entity.locations ?? []).filter(
    (point) => point.city?.trim() && typeof point.lat === 'number' && typeof point.lng === 'number',
  ).map((point) => ({
    city: point.city.trim(),
    ...(point.state?.trim() ? { state: point.state.trim() } : {}),
    lat: point.lat,
    lng: point.lng,
  }));
}

export function formatBountyLocationPoint(point: BountyLocationPoint): string {
  return point.state?.trim() ? `${point.city}, ${point.state}` : point.city;
}

/** Human-readable location label for tables and read-only views. */
export function formatBountyLocationsLabel(entity: BountyLocationFields): string | undefined {
  if (entity.isNational) return 'National';
  const points = getBountyLocationPoints(entity);
  if (points.length === 0) return undefined;
  const first = formatBountyLocationPoint(points[0]);
  if (points.length === 1) return first;
  return `${first} +${points.length - 1}`;
}

const locationPointKey = (point: BountyLocationPoint): string =>
  `${point.city.trim().toLowerCase()}|${(point.state ?? '').trim().toLowerCase()}|${point.lat}|${point.lng}`;

/** Normalize location input into Firestore write fields. */
export function buildBountyCompanyLocationWrite(input: BountyCompanyLocationInput): {
  isNational?: boolean;
  locations?: BountyLocationPoint[];
  clearLocation?: boolean;
} {
  if (input.isNational === true) {
    return { isNational: true, locations: [], clearLocation: true };
  }

  if (input.locations === undefined) {
    return input.isNational !== undefined ? { isNational: input.isNational } : {};
  }

  const seen = new Set<string>();
  const locations = input.locations.filter((point) => {
    if (!point.city?.trim() || typeof point.lat !== 'number' || typeof point.lng !== 'number') return false;
    const key = locationPointKey(point);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((point) => ({
    city: point.city.trim(),
    ...(point.state?.trim() ? { state: point.state.trim() } : {}),
    lat: point.lat,
    lng: point.lng,
  }));

  if (locations.length === 0) {
    return {
      locations: [],
      clearLocation: true,
      ...(input.isNational !== undefined ? { isNational: input.isNational } : {}),
    };
  }

  return { locations, isNational: false };
}
